import { useState, useEffect } from "react";

import getLogger, { LogGroup } from "js/core/logger";
import { getExperiments } from "js/core/services/experiments";

const logger = getLogger(LogGroup.APP);

interface ExperimentState {
    loading: boolean;
    experiments: Record<string, boolean>;
    error: Error | null;
}

export function useExperiments(ids: string[]): ExperimentState {
    const [state, setState] = useState<ExperimentState>({
        loading: true,
        experiments: {},
        error: null
    });

    const idsKey = ids.sort().join("|");

    useEffect(() => {
        const abortController = new AbortController();

        const fetchExperiments = async () => {
            try {
                setState(prev => ({ ...prev, loading: true, error: null }));

                const experiments = await getExperiments(ids);

                if (abortController.signal.aborted) return;

                const experimentResults: Record<string, boolean> = {};
                ids.forEach(id => {
                    experimentResults[id] = experiments[id]?.enabled || false;
                });

                setState({
                    loading: false,
                    experiments: experimentResults,
                    error: null
                });
            } catch (err) {
                logger.error(err, "[useExperiments] fetchExperiments() failed");

                if (abortController.signal.aborted) return;

                const fallbackResults: Record<string, boolean> = {};
                ids.forEach(id => {
                    fallbackResults[id] = false;
                });

                setState({
                    loading: false,
                    experiments: fallbackResults,
                    error: err as Error
                });
            }
        };

        if (ids.length > 0) {
            fetchExperiments();
        } else {
            setState({
                loading: false,
                experiments: {},
                error: null
            });
        }

        return () => {
            abortController.abort();
        };
    }, [idsKey]);

    return state;
}
